import * as yup from "yup";
import React, { useCallback, Fragment, useEffect, useMemo } from "react";
import { useFormik } from "formik";

import groupBy from "lodash/groupBy";

import JourneysSelect from "../shared/JourneysSelect";
import StimuliSelect from "../shared/StimuliSelect";
import { useAppContext } from "../../api/AppContext";
import RadioGroup from "@material-ui/core/RadioGroup";
import FormLabel from "@material-ui/core/FormLabel";
import FormControl from "@material-ui/core/FormControl";
import Radio from "@material-ui/core/Radio";
import styled from "styled-components/macro";

import { FormControlLabel } from "@material-ui/core";
import Box from "@material-ui/core/Box";
import Button from "@material-ui/core/Button";
import CircularProgress from "@material-ui/core/CircularProgress";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import Grid from "@material-ui/core/Grid";
import IconButton from "@material-ui/core/IconButton";
import Typography from "@material-ui/core/Typography";
import Alert from "@material-ui/lab/Alert";

import CloseIcon from "@material-ui/icons/Close";

import Dialog from "../shared/Dialog";
import DialogActions from "../DialogActions";
import useConfirmLeave from "../../hooks/useConfirmLeave";
import useModal from "../../hooks/useModal";
import { getFieldProps } from "../utils";
import * as Yup from "yup";
import Checkbox from "@material-ui/core/Checkbox";

const LoaderContainer = styled.div`
  display: flex;
  justify-content: center;
  height: 100%;
  align-items: center;
  flex-direction: column;
`;

const validationSchema = () =>
  yup.object().shape({
    copyLearningMaterial: yup.boolean(),
    copyScheduledSlot: yup.boolean(),
    nudgeStimulusId: Yup.string().when("targetFlowId", {
      is: (val) => val === "Nudge",
      otherwise: (schema) => schema.nullable(),
      then: (schema) => schema.required(),
    }),
    targetFlowType: yup.mixed().oneOf(["Tip", "Nudge"]).required(),
    targetJourneyId: Yup.array().of(Yup.string()).min(1).required(),
  });

const CopyFlowModal = ({ journeyId, flowId, onClose }) => {
  const {
    journeys,
    otherJourneyTriggers,
    fetchTriggers,
    isTriggersLoading,
    isJourneysLoading,
    copySemanticFlow,
    semantics,
    fetchSemantics,
    isSemanticsLoading,
    materials,
    flowRelations,
    fetchFlowRelations,
    scheduledSlots,
    fetchScheduledSlots,
  } = useAppContext(); // fetchJourneys, fetchTriggers

  // Get current flow's semantics
  const currentFlowSemantics = useMemo(
    () => semantics.filter((semantic) => semantic.flowId === flowId),
    [semantics, flowId]
  );

  const triggersByStimulusId = useMemo(
    () =>
      otherJourneyTriggers
        ? Object.values(groupBy(otherJourneyTriggers, "stimulusId"))
        : [],
    [otherJourneyTriggers]
  );

  const stimulusOptions = useMemo(
    // there are multiple triggers with same stimulusId; take the first one to display a name
    // Note: trigger id is usually equal to its name; stimulusId is in stimulusId field
    () =>
      triggersByStimulusId
        ? triggersByStimulusId.map(([{ name, stimulusId }, ...triggers]) => {
            return { id: stimulusId, name };
          })
        : [],
    [triggersByStimulusId]
  );

  const onConfirm = useCallback(
    async (data) => {
      const discardedCtaInfo = await copySemanticFlow({
        flowId,
        formData: data,
        journeyId,
      });
      console.log("discardedCtaInfo => ", JSON.stringify(discardedCtaInfo));
      onClose();
    },
    [copySemanticFlow, flowId, journeyId, onClose]
  );

  const { handleConfirm } = useModal({ onClose, onConfirm });

  const formik = useFormik({
    initialValues: {
      copyLearningMaterial: false,
      copyScheduledSlot: false,
      nudgeStimulusId: null,
      targetFlowType: "Tip",
      targetJourneyId: [],
    },
    onSubmit: (values) => {
      handleConfirm({
        ...values,
      });
    },
    validationSchema: validationSchema(),
    validator: () => ({}),
  });

  const { onClose: handleClose } = useConfirmLeave({
    formik,
    onClose,
  });

  // Detecting if flow contains Dashboard Material CTAs
  const hasLearningMaterialCTAs = useMemo(() => {
    if (!currentFlowSemantics.length) return false;

    return currentFlowSemantics.some((semantic) => {
      return (
        semantic.cta1Type === "Dashboard Material" ||
        semantic.cta2Type === "Dashboard Material" ||
        semantic.dropdownData?.some(
          (option) => option.ctaType === "Dashboard Material"
        )
      );
    });
  }, [currentFlowSemantics]);

  // Detecting if flow has scheduled slot
  const hasScheduledSlotCTAs = useMemo(() => {
    if (!flowRelations.length) return false;

    return flowRelations.some(
      (relation) =>
        relation.flowId === flowId && relation.type === "scheduledSlot"
    );
  }, [flowRelations, flowId]);

  // Detecting if flow has scheduled slot with unsupported condition types
  const hasUnsupportedScheduledSlotConditions = useMemo(() => {
    if (!flowRelations.length || !scheduledSlots.length) return false;

    const unsupportedConditionTypes = [
      "subscribedToWelfare",
      "survey",
      "lmconsumed",
    ];

    const scheduledSlotIds = flowRelations
      .filter(
        (relation) =>
          relation.flowId === flowId && relation.type === "scheduledSlot"
      )
      .map((relation) => relation.relationId);

    return scheduledSlots.some((scheduledSlot) => {
      if (!scheduledSlotIds.includes(scheduledSlot.id)) return false;

      const hasUnsupported = scheduledSlot.conditions?.some((condition) =>
        unsupportedConditionTypes.includes(condition.type)
      );

      return hasUnsupported;
    });
  }, [flowRelations, scheduledSlots, flowId]);

  // Detecting if scheduled slot is today or in the past
  const hasScheduledSlotTodayOrPast = useMemo(() => {
    if (!flowRelations.length || !scheduledSlots.length) return false;

    const scheduledSlotIds = flowRelations
      .filter(
        (relation) =>
          relation.flowId === flowId && relation.type === "scheduledSlot"
      )
      .map((relation) => relation.relationId);

    const startOfToday = new Date();
    startOfToday.setHours(0, 0, 0, 0);
    const endOfToday = startOfToday.getTime() + 24 * 60 * 60 * 1000 - 1;

    return scheduledSlots.some((scheduledSlot) => {
      if (!scheduledSlotIds.includes(scheduledSlot.id)) return false;
      return scheduledSlot.datetimeStart <= endOfToday;
    });
  }, [flowRelations, scheduledSlots, flowId]);

  const shouldShowCopyLearningMaterial = useMemo(() => {
    return formik.values.targetFlowType === "Tip" && hasLearningMaterialCTAs;
  }, [formik.values.targetFlowType, hasLearningMaterialCTAs]);

  const shouldShowCopyScheduledSlot = useMemo(() => {
    return formik.values.targetFlowType === "Tip" && hasScheduledSlotCTAs;
  }, [formik.values.targetFlowType, hasScheduledSlotCTAs]);

  // Checking for unsupported CTA types
  const hasUnsupportedCTAs = useMemo(() => {
    if (!currentFlowSemantics.length) return false;

    const unsupportedTypes = ["Welfare", "Dashboard Step", "Survey"];

    return currentFlowSemantics.some((semantic) => {
      return (
        unsupportedTypes.includes(semantic.cta1Type) ||
        unsupportedTypes.includes(semantic.cta2Type) ||
        semantic.dropdownData?.some((option) =>
          unsupportedTypes.includes(option.ctaType)
        )
      );
    });
  }, [currentFlowSemantics]);

  // Checking for Dashboard Material CTAs with unsupported types
  const hasUnsupportedDashboardMaterials = useMemo(() => {
    if (!currentFlowSemantics.length || !materials.length) return false;

    const unsupportedMaterialTypes = ["SEMANTIC", "SURVEY"];

    const dashboardMaterialIds = [];

    currentFlowSemantics.forEach((semantic) => {
      if (semantic.cta1Type === "Dashboard Material" && semantic.cta1LinkId) {
        dashboardMaterialIds.push(semantic.cta1LinkId);
      }
      if (semantic.cta2Type === "Dashboard Material" && semantic.cta2LinkId) {
        dashboardMaterialIds.push(semantic.cta2LinkId);
      }

      semantic.dropdownData?.forEach((option) => {
        if (option.ctaType === "Dashboard Material" && option.ctaTarget) {
          dashboardMaterialIds.push(option.ctaTarget);
        }
      });
    });

    return dashboardMaterialIds.some((materialId) => {
      const material = materials.find((m) => m.id === materialId);
      return material && unsupportedMaterialTypes.includes(material.type);
    });
  }, [currentFlowSemantics, materials]);

  useEffect(() => {
    if (!semantics.length && !isSemanticsLoading) {
      fetchSemantics({ journeyId });
    }
  }, [semantics.length, isSemanticsLoading, fetchSemantics, journeyId]);

  useEffect(() => {
    if (!flowRelations.length) {
      fetchFlowRelations({ journeyId });
    }
  }, [flowRelations.length, fetchFlowRelations, journeyId]);

  useEffect(() => {
    if (!scheduledSlots.length) {
      fetchScheduledSlots({ journeyId });
    }
  }, [scheduledSlots.length, fetchScheduledSlots, journeyId]);

  useEffect(() => {
    if (
      formik.values.targetFlowType === "Nudge" &&
      formik.values.targetJourneyId.length > 0
    ) {
      fetchTriggers({ otherJourneyId: formik.values?.targetJourneyId });
    }
  }, [
    fetchTriggers,
    formik.values.targetJourneyId,
    formik.values.targetFlowType,
  ]);

  const handleNudgeStimulusIdChange = useCallback(
    (value) => {
      formik.setFieldValue("nudgeStimulusId", value);
    },
    [formik]
  );

  const handleTargetJourneyIdChange = useCallback(
    (value) => {
      formik.setFieldValue("targetJourneyId", value);
      formik.setFieldValue("nudgeStimulusId", null); // reset stimulus selection
    },
    [formik]
  );

  // TODO remember last selections and reapply here..
  // useEffect(() => {
  //   formik.values.targetJourneyId =
  // }, [formik]);

  return (
    <Dialog fullWidth open maxWidth="md" onClose={handleClose}>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between">
          Copy a flow
          <IconButton size="small" onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent style={{ overflowY: "visible" }}>
        <Grid container spacing={4}>
          <Grid item xs={12}>
            {isJourneysLoading ? (
              <LoaderContainer>
                <CircularProgress size={30} />
              </LoaderContainer>
            ) : (
              <JourneysSelect
                {...getFieldProps(formik, {
                  name: "targetJourneyId",
                })}
                journeys={journeys}
                label="Target Journey"
                multiple={true}
                onChange={handleTargetJourneyIdChange}
              />
            )}
          </Grid>
          <Grid item xs={12}>
            <FormControl>
              <FormLabel id="flowtypelabel">Destination flow type:</FormLabel>
              <RadioGroup
                aria-labelledby="flowtypelabel"
                name="targetFlowType"
                value={formik.values.targetFlowType}
                onChange={formik.handleChange}
              >
                {[
                  { description: "Tip", value: "Tip" },
                  { description: "Nudge", value: "Nudge" },
                ].map(({ description, value }) => (
                  <Fragment key={value}>
                    <FormControlLabel
                      control={<Radio color="primary" />}
                      label={
                        <Typography color="textSecondary" variant="body2">
                          {description}
                        </Typography>
                      }
                      value={value}
                    />
                  </Fragment>
                ))}
              </RadioGroup>
            </FormControl>
          </Grid>
          {formik.values.targetJourneyId.length > 0 &&
            formik.values.targetFlowType === "Nudge" &&
            stimulusOptions.length && (
              <Grid item xs={12}>
                {isTriggersLoading ? (
                  <LoaderContainer>
                    <CircularProgress size={30} />
                  </LoaderContainer>
                ) : (
                  <StimuliSelect
                    {...getFieldProps(formik, {
                      name: "nudgeStimulusId",
                    })}
                    label="Stimulus"
                    stimuli={stimulusOptions}
                    onChange={handleNudgeStimulusIdChange}
                  />
                )}
              </Grid>
            )}
          {shouldShowCopyLearningMaterial && (
            <Grid item style={{ paddingBlock: 0 }} xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formik.values.copyLearningMaterial}
                    color="primary"
                    onChange={(event) =>
                      formik.setFieldValue(
                        "copyLearningMaterial",
                        event.target.checked
                      )
                    }
                  />
                }
                label={
                  <Typography color="textSecondary" variant="body2">
                    Copy Learning Material
                  </Typography>
                }
              />
            </Grid>
          )}
          {shouldShowCopyScheduledSlot && (
            <Grid item style={{ paddingBlock: 0 }} xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formik.values.copyScheduledSlot}
                    color="primary"
                    onChange={(event) =>
                      formik.setFieldValue(
                        "copyScheduledSlot",
                        event.target.checked
                      )
                    }
                  />
                }
                label={
                  <Typography color="textSecondary" variant="body2">
                    Copy Scheduled Slot
                  </Typography>
                }
              />
            </Grid>
          )}
          {hasUnsupportedCTAs && (
            <Grid item xs={12}>
              <Alert severity="warning" variant="outlined">
                <Typography variant="body2">
                  Note: This flow contains unsupported CTA types which will be
                  reset to &quot;TBD&quot; during copy.
                </Typography>
              </Alert>
            </Grid>
          )}
          {hasUnsupportedDashboardMaterials && (
            <Grid item xs={12}>
              <Alert severity="warning" variant="outlined">
                <Typography variant="body2">
                  Note: This flow contains CTA type of Dashboard Material with
                  unsupported types which will not be copied correctly.
                </Typography>
              </Alert>
            </Grid>
          )}
          {(hasScheduledSlotTodayOrPast ||
            hasUnsupportedScheduledSlotConditions) && (
            <Grid item xs={12}>
              <Alert severity="warning" variant="outlined">
                <Typography variant="body2">
                  {hasScheduledSlotTodayOrPast
                    ? "Note: This flow contains scheduled slots with unsupported condition types which will have their start datetime set to future date during copy to make them ineffective."
                    : "Note: This flow contains scheduled slots with unsupported condition types which will not be copied correctly."}
                </Typography>
              </Alert>
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button
          color="primary"
          disabled={
            !formik.dirty ||
            formik.isSubmitting ||
            (formik.values.targetFlowType === "Nudge" &&
              !formik.values?.nudgeStimulusId)
          }
          endIcon={formik.isSubmitting && <CircularProgress size={25} />}
          variant="contained"
          onClick={formik.handleSubmit}
        >
          Save
        </Button>
        <Button
          color="primary"
          disabled={formik.isSubmitting}
          variant="outlined"
          onClick={handleClose}
        >
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CopyFlowModal;
